// Shared helper — determines whether a charter guest is scheduled to be
// onboard at a specific date/time. Mirrors the backend
// `_guest_currently_onboard` semantics in `server.py` so the manual-booking
// pickers agree with what the yacht's log will accept.
//
// Resolution order (first hit wins):
//   1. `stays[]` — any segment covering `iso` counts as onboard.
//   2. Top-level `embark_at` / `disembark_at` on the guest doc (legacy).
//   3. Charter window fallback — `charter_arrival` <= iso <= `charter_departure`
//      when the guest is enriched with those fields. Prevents a legacy row
//      without any per-guest dates from being booked outside the charter.
//
// If NONE of the three sources define a window, the guest is treated as
// "always onboard" so a fresh seed with no dates at all keeps working.

function stayCovers(seg, iso) {
  const ea = seg?.embark_at || "";
  const da = seg?.disembark_at || "";
  if (!ea && !da) return false;
  return (!ea || ea <= iso) && (!da || da > iso);
}

export function guestOnboardAt(guest, isoDate) {
  if (!guest || !isoDate) return false;

  // 1) Per-guest stays[] — explicit multi-segment schema.
  if (Array.isArray(guest.stays) && guest.stays.length > 0) {
    const some = guest.stays.some((s) => (s?.embark_at || s?.disembark_at) && stayCovers(s, isoDate));
    if (some) return true;
    // If any stay row DEFINES a window and none covers `iso`, refuse — the
    // guest has explicit dates and they don't include this slot.
    const anyDates = guest.stays.some((s) => s?.embark_at || s?.disembark_at);
    if (anyDates) return false;
  }

  // 2) Legacy top-level fields.
  const topEa = guest.embark_at || "";
  const topDa = guest.disembark_at || "";
  if (topEa || topDa) {
    return (!topEa || topEa <= isoDate) && (!topDa || topDa > isoDate);
  }

  // 3) Charter-window fallback — when the guest has no explicit dates at
  //    all, treat their implicit stay as spanning the whole charter.
  const chA = guest.charter_arrival || "";
  const chD = guest.charter_departure || "";
  if (chA || chD) {
    return (!chA || chA <= isoDate) && (!chD || chD >= isoDate);
  }

  // 4) Absolute fallback — no dates anywhere; assume onboard (seed data).
  return true;
}
